import { VehicleCatalogMarket, VehicleCatalogMarketLabels } from '@navio/nsdk';
import { cn, NoddiIcon } from 'noddi-ui-common';
import { NoddiBadge, NoddiTooltip } from 'noddi-ui-web';
import { getLabelMapFromEntries } from 'noddi-util';

const MARKET_LABEL = getLabelMapFromEntries(VehicleCatalogMarket, VehicleCatalogMarketLabels);

const marketText = (market: VehicleCatalogMarket) => `${market.toUpperCase()}: ${MARKET_LABEL[market]}`;

type Props = {
  markets: VehicleCatalogMarket[];
  /** Badges shown before the rest collapse into one `+n`; the row sits beside a name, not instead of it. */
  max?: number;
  className?: string;
};

/**
 * The sales regions a catalog node is published for: the code, with the region named on hover. The
 * regions are a field on the node -- never parsed back out of the name the provider wrote them into.
 */
export const CatalogMarketBadges = ({ markets, max = 3, className }: Props) => {
  if (markets.length === 0) {
    return null;
  }
  const shown = markets.slice(0, max);
  const rest = markets.slice(max);
  return (
    <div className={cn('flex flex-wrap items-center gap-1', className)} role='group' aria-label='Sales regions'>
      {shown.map((market) => (
        <NoddiTooltip
          key={market}
          content={MARKET_LABEL[market]}
          label={marketText(market)}
          trigger={
            <NoddiBadge variant='outline' aria-label={marketText(market)}>
              <NoddiIcon name='Globe' size='small' />
              {market.toUpperCase()}
            </NoddiBadge>
          }
        />
      ))}
      {rest.length > 0 && (
        <NoddiTooltip
          label='More sales regions'
          content={rest.map(marketText).join(', ')}
          trigger={
            <NoddiBadge variant='outline'>
              <NoddiIcon name='Globe' size='small' />+{rest.length}
            </NoddiBadge>
          }
        />
      )}
    </div>
  );
};
